import { Column, Index } from 'typeorm';
import type { ApolloSpendAction, ApolloSpendResult } from '../../constants/apollo';
import { Entity } from '../../decorators';
import { BigIntBaseEntity } from './base.entity';

/**
 * PD-17611 — the Apollo credit log. One row per Apollo people search, reveal
 * and company lookup (who, what, credits, result), written by
 * ApolloClientService, the one gate every Apollo call goes through. It feeds
 * the Apollo settings page (credits this month, who spent what, the miss
 * rate) and the limit checks (a monthly Workd budget, per-role monthly
 * limits). A spend a limit stopped is logged too, with no credits, so an
 * admin can see who was stopped.
 *
 * Kept apart from the AI usage ledger on purpose (Chris, 10/26/26): Apollo
 * credits are the tenant's own Apollo plan, not Workd AI credits.
 *
 * Append-only and not audited (`auditable: false`): a row is never changed,
 * so an `_audit` row per insert would record nothing the row does not (the
 * `ai_usage_event` reasoning).
 *
 * The user column is a plain tenant `user._id` without a relation, so a row
 * outlives a removed user; null means Workd itself spent it (a nightly or
 * automatic job).
 *
 * SM1 exposure: none. SM1 names no such table.
 */
@Entity('apollo_spend', {}, { auditable: false })
@Index('IDX_apollo_spend_period_user', ['periodKey', 'fkUserId'])
@Index('IDX_apollo_spend_period_action', ['periodKey', 'action'])
@Index('IDX_apollo_spend_action_reference', ['action', 'reference'])
// PD-17612: Apollo's contacts/search limits (per minute, hour and day) are
// counted from this log, so the count reads by action and time.
@Index('IDX_apollo_spend_action_occurred', ['action', 'occurredAt'])
export class ApolloSpendEntity extends BigIntBaseEntity {
	@Column('datetime', { name: 'occurredAt', precision: 3 })
	public occurredAt!: Date;

	/** `YYYY-MM`, the calendar month in the tenant's zone (aiUsagePeriodKey). */
	@Column('char', { name: 'periodKey', length: 7 })
	public periodKey!: string;

	/** Tenant `user._id` of the person who spent it; null for Workd's own jobs. */
	@Column('int', { name: 'fk_user__id', unsigned: true, nullable: true })
	public fkUserId!: number | null;

	@Column('varchar', { name: 'action', length: 24 })
	public action!: ApolloSpendAction;

	/** The Workd feature that asked (find_people, fill_missing, bounce_follow_up, ...). */
	@Column('varchar', { name: 'feature', length: 48, nullable: true })
	public feature!: string | null;

	@Column('int', { name: 'credits', unsigned: true, default: 0 })
	public credits!: number;

	/** People or companies the call returned (a search) or revealed (a reveal). */
	@Column('int', { name: 'count', unsigned: true, default: 0 })
	public count!: number;

	/**
	 * Results Workd already had and left out without paying: search results
	 * already on a Workd person or revealed before. The settings page shows
	 * the month's total as "Already ours".
	 */
	@Column('int', { name: 'matchedCount', unsigned: true, default: 0 })
	public matchedCount!: number;

	/** The Apollo person id, the company domain, or a short search summary. */
	@Column('varchar', { name: 'reference', length: 255, nullable: true })
	public reference!: string | null;

	@Column('varchar', { name: 'result', length: 24 })
	public result!: ApolloSpendResult;

	/**
	 * The request and a summary of the answer: a search's filters and total,
	 * or a company lookup's company, so a repeated lookup of the same domain
	 * is answered from the log instead of paying again.
	 */
	@Column('json', { name: 'details', nullable: true })
	public details!: Record<string, unknown> | null;
}
